// api/assistants.ts
import { SiteBotConfig } from '../components/static/Site/SiteBotConfigForm';
import axiosInstance from './axiosInstance';
import { AddSitePayload, ApiResponse, BotAnalyticsData, BotAnalyticsResponse, BotDetails, BotItem, CanvasBotDetails, CreateAssistantPayload, CreateAssistantResponse, CreateCanvasAssistantPayload, CreateThreadResponse, DeletePayload, SiteBotDetails, SiteRecord, ThreadAnalytics } from './types';

export const createCanvasAssistant = async (
  payload: CreateCanvasAssistantPayload
): Promise<ApiResponse<CreateAssistantResponse>> => {
  try {
    const { name, files, profileImage, focus, instructions } = payload;

    const form = new FormData();

    // Scalars your backend expects
    form.append("name", name);
    form.append("focus", focus);
    form.append("type", "canvas"); // mark this as a canvas bot
    form.append("instructions", instructions);

    // JSON metadata blob
    form.append(
      "metadata",
      JSON.stringify({
        profileImage,
      })
    );

    // Support up to 5 file uploads
    files.slice(0, 5).forEach((f) => form.append("files", f));

    const res = await axiosInstance.post<CreateAssistantResponse>(
      "/v2/bot/create",
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return { data: res.data, error: null };
  } catch (err: any) {
    const errorMessage =
      err.response?.data?.message || err.message || "Unknown error";
    return { data: null, error: errorMessage };
  }
};

export const createResumeAssistant = async (
  payload: CreateAssistantPayload
): Promise<ApiResponse<CreateAssistantResponse>> => {
  try {
    const {
      name,
      location,
      spokenLanguages,
      keywords,
      experiences,
      files,
      profileImage,
      focus,
      questions,
    } = payload;

    const form = new FormData();

    // Scalars your validateBodyFields() may depend on
    form.append('name', name);
    form.append('focus', focus);
    form.append("type", "resume");

    // Pack everything else into one JSON field so multer can still parse it
    form.append(
      'metadata',
      JSON.stringify({
        location,
        spokenLanguages,
        keywords,
        experiences,
        profileImage,
        questions,
      })
    );

    files.slice(0, 5).forEach((f) => form.append('files', f));

    const res = await axiosInstance.post<CreateAssistantResponse>(
      `/v2/bot/create`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    return { data: res.data, error: null };
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
    return { data: null, error: errorMessage };
  }
};

export type CreateSiteAssistantPayload = {
  config: SiteBotConfig;         // from your form (includes name, company, personality, languages, etc.)
  files: File[];                 // from your uploader
  profileImage: string;
  focus: string;                 // keep as before
};

export const createSiteAssistant = async (
  payload: CreateSiteAssistantPayload
): Promise<ApiResponse<CreateAssistantResponse>> => {
  try {
    const { config, files = [], profileImage, focus } = payload;

    // Ensure a safe default for languages if form disabled them
    const languages =
      Array.isArray(config.languages) && config.languages.length > 0
        ? config.languages
        : ["English"];

    const form = new FormData();

    // Scalars your server-side validateBodyFields() may depend on
    form.append("name", config.name ?? ""); // pulled from config
    form.append("focus", focus ?? "");
    form.append("type", "site");

    // Build metadata (everything non-file so multer can still parse)
    const metadata: any = {
      config: {
        ...config,
        languages, // enforce default
        profileImage,
      },
    };

    form.append("metadata", JSON.stringify(metadata));

    // Files (cap at 5, as before)
    files.slice(0, 5).forEach((f) => form.append("files", f));

    const res = await axiosInstance.post<CreateAssistantResponse>("/v2/bot/create", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return { data: res.data, error: null };
  } catch (err: any) {
    const errorMessage = err?.response?.data?.message || err.message || "Unknown error";
    return { data: null, error: errorMessage };
  }
};

export const getAssistantList = async (): Promise<ApiResponse<BotItem[]>> => {
  try {
    const res = await axiosInstance.get<{ bots: BotItem[] }>('/bot/list');
    return { data: res.data.bots, error: null };
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
    return { data: null, error: errorMessage };
  }
};

export const deleteAssistant = async (payload: DeletePayload): Promise<ApiResponse<any>> => {
  try {
    const res = await axiosInstance.post<{status: boolean}>('/v2/bot/delete', payload);

    return { data: res.data, error: null };
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
    return { data: null, error: errorMessage };
  }
};

export const getBotAnalyticsList = async (): Promise<ApiResponse<BotAnalyticsData[]>> => {
  try {
    const res = await axiosInstance.get<{
      success: boolean;
      data: Array<{
        id: string;
        name: string;
        type: string;
        focus: string
        enabled: boolean,
        activeThreads: number,
        inactiveThreads: number,
        totalUserMessages: number,
        stats?: BotAnalyticsResponse['stats'];
        threadSummaries?: ThreadAnalytics[];
      }>;
    }>('/bot/analytics');
    const transformed: BotAnalyticsData[] = res.data.data.map((bot) => ({
      id: bot.id,
      name: bot.name || 'Untitled Bot',
      type: bot.type,
      focus: bot.focus,
      enabled: bot.enabled,
      activeThreads: bot.activeThreads ?? 0,
      inactiveThreads: bot.inactiveThreads ?? 0,
      totalUserMessages: bot.totalUserMessages ?? 0,
      threadSummaries: (bot.threadSummaries ?? []).map((thread) => ({
        threadId: thread.threadId,
        createdAt: thread.messages?.[0]?.createdAt ?? Date.now(),
        messages: (thread.messages ?? []).map((m) => ({
          sender: m.sender,
          content: m.content,
          createdAt: m.createdAt,
        })),
      })),
    }));

    return { data: transformed, error: null };
  } catch (err: any) {
    const errorMessage = err.response?.data?.msg || err.message || 'Unknown error';
    return { data: null, error: errorMessage };
  }
};

export const getBotDetails = async (botId: string): Promise<ApiResponse<BotDetails>> => {
  try {
    const res = await axiosInstance.get<{ success: boolean; data: BotDetails }>(`/bot/details/${botId}`);

    if (res.data.success) {
      return { data: res.data.data, error: null };
    } else {
      return { data: null, error: 'Failed to fetch bot details' };
    }
  } catch (err: any) {
    const errorMessage = err.response?.data?.msg || err.message || 'Unknown error';
    return { data: null, error: errorMessage };
  }
};

export const getSiteBotDetails = async (
  botId: string
): Promise<ApiResponse<SiteBotDetails>> => {
  try {
    const res = await axiosInstance.get<{ success: boolean; data: SiteBotDetails }>(
      `/bot/site/details/${botId}`
    );

    if (res.data.success) {
      return { data: res.data.data, error: null };
    } else {
      return { data: null, error: 'Failed to fetch site bot details' };
    }
  } catch (err: any) {
    const errorMessage = err.response?.data?.msg || err.message || 'Unknown error';
    return { data: null, error: errorMessage };
  }
};

export const getCanvasBotDetails = async (
  botId: string
): Promise<ApiResponse<CanvasBotDetails>> => {
  try {
    const res = await axiosInstance.get<{ success: boolean; data: CanvasBotDetails }>(
      `/bot/canvas/details/${botId}`
    );

    if (res.data.success) {
      return { data: res.data.data, error: null };
    } else {
      return { data: null, error: 'Failed to fetch site bot details' };
    }
  } catch (err: any) {
    const errorMessage = err.response?.data?.msg || err.message || 'Unknown error';
    return { data: null, error: errorMessage };
  }
};

export const createThreadWithBot = async (
  botId: string,
  message: string
): Promise<ApiResponse<CreateThreadResponse>> => {
  try {
    const res = await axiosInstance.post<{
      success: boolean;
      threadId: string;
      message: string;
      usage?: any;
    }>('/v2/chat/create', { botId, message });

    return {
      data: {
        threadId: res.data.threadId,
        message: res.data.message,
        usage: res.data.usage,
      },
      error: null,
    };
  } catch (err: any) {
    const errorMessage = err.response?.data?.msg || err.message || 'Unknown error';
    return {
      data: null,
      error: errorMessage,
    };
  }
};


export const chatWithBot = async (
  botId: string,
  message: string,
  threadId: string
): Promise<ApiResponse<CreateThreadResponse>> => {
  try {
    const res = await axiosInstance.post<{
      success: boolean;
      threadId: string;
      message: string;
      usage?: any;
    }>('/v2/chat', { botId, message, threadId });

    return {
      data: {
        threadId: res.data.threadId,
        message: res.data.message,
        usage: res.data.usage,
      },
      error: null,
    };
  } catch (err: any) {
    const errorMessage = err.response?.data?.msg || err.message || 'Unknown error';
    return {
      data: null,
      error: errorMessage,
    };
  }
};

export type BotViewTime = {
  botName: string;
  averageViewTimeMinutes: number;
};

export type WeeklyPoint = {
  date: string;
  value: number;
};

export type DashboardSummary = {
  totalBots: number;
  totalThreads: number;
  totalMessages: number;
  allBotViews: number;
  visitorsPerBot: {
    botId: string;
    name: string;
    visitors: number;
  }[];
  recentMessages: {
    botId: string;
    message: string;
    timestamp: string; // If you're converting Date to JSON
  }[];
  botViewTimes: BotViewTime[];
  dailyVisitsLast7Days: WeeklyPoint[];
};


export const getDashboardSummary = async (): Promise<ApiResponse<DashboardSummary>> => {
  try {
    const res = await axiosInstance.post<{ success: boolean; data: DashboardSummary }>(
      '/dashboard/summary'
    );

    if (res.status === 200 && res.data) {
      return { data: res.data as unknown as DashboardSummary, error: null };
    } else {
      return { data: null, error: 'Failed to fetch dashboard summary' };
    }
  } catch (err: any) {
    const errorMessage = err.response?.data?.msg || err.message || 'Unknown error';
    return { data: null, error: errorMessage };
  }
};

// GET /v2/bot/sites?botId=...
export const loadSites = async (botId: string): Promise<ApiResponse<SiteRecord[]>> => {
  try {
    const res = await axiosInstance.get<{ sites: Array<SiteRecord | string> }>("/bot/sites", {
      params: { botId },
    });

    // Normalize to SiteRecord[]
    const normalized: SiteRecord[] = Array.isArray(res.data?.sites)
      ? res.data.sites.map((s) => (typeof s === "string" ? { url: s } : s))
      : [];

    return { data: normalized, error: null };
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || err.message || "Unknown error";
    return { data: null, error: errorMessage };
  }
};

// POST /v2/bot/sites/add  { botId, url }
export const addSite = async (payload: AddSitePayload): Promise<ApiResponse<SiteRecord>> => {
  try {
    const res = await axiosInstance.post<{ site: SiteRecord }>("/bot/sites/add", payload);
    // Some backends might return { status:true, site:{...} } or just { url:"..." }
    const siteRaw = (res.data as any)?.site ?? res.data;
    const normalized: SiteRecord =
      typeof siteRaw === "string" ? { url: siteRaw } : (siteRaw as SiteRecord);

    return { data: normalized, error: null };
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || err.message || "Unknown error";
    return { data: null, error: errorMessage };
  }
};

export const removeSite = async (payload: AddSitePayload): Promise<ApiResponse<SiteRecord>> => {
  try {
    const res = await axiosInstance.post<{ site: SiteRecord }>("/bot/sites/remove", payload);
    // Some backends might return { status:true, site:{...} } or just { url:"..." }
    const siteRaw = (res.data as any)?.site ?? res.data;
    const normalized: SiteRecord =
      typeof siteRaw === "string" ? { url: siteRaw } : (siteRaw as SiteRecord);

    return { data: normalized, error: null };
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || err.message || "Unknown error";
    return { data: null, error: errorMessage };
  }
};

